import { Button, Table, Tag, message, Card, Space } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import socialsIcon from '../../assets/images/socials.svg';

const SOURCE_IDS = {
    B2B: '7c8f3009-d244-4ee8-8a44-b71acf5af159',
    EXCEL: 'bd21964e-2155-449e-8e9f-58bac2f11bb8',
    DIGITAL: 'c43d9c47-2734-4c29-a08d-a50442a89aaf'
};

const GeneralSalesIcon = () => (
    <img src={socialsIcon} width={30} height={30} alt="Обсяг продажу" />
);

const SummaryRow = ({ totals, mode }) => {
    const formatNumber = (number) => {
        if (number === undefined || number === null) return '0,00';
        return number
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            .replace('.', ',');
    };

    const isComparison = mode === 'comparison';

    const items = isComparison ? [
        { label: 'Місячний звіт B2B', value: totals.monthlyTotal, color: '#2d1b4e' },
        { label: 'Місячний звіт Цифр.', value: totals.digitalTotal, color: '#2d1b4e' },
        { label: 'Розбіжність (Total)', value: Math.abs(totals.digitalTotal - totals.monthlyTotal), color: '#ff4d4f', isBold: true },
    ] : [
        { label: 'Місячний звіт Цифр.', value: totals.digitalTotal, color: '#9A18FF', isBold: true },
    ];

    return (
        <Card bordered={false} className="summary-bar-card" style={{
            borderRadius: '12px',
            marginBottom: '32px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
            background: '#fff',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
        }} bodyStyle={{ padding: '20px 24px' }}>
            <div style={{
                display: 'flex',
                flexWrap: 'nowrap',
                justifyContent: 'space-between',
                gap: '24px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}>
                <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                    {items.map((item, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto'
                        }}>
                            <span style={{ color: '#8c8c8c', fontSize: '14px', fontWeight: 500 }}>{item.label}:</span>
                            <span style={{
                                color: item.color,
                                fontWeight: item.isBold ? 800 : 700,
                                fontSize: item.isBold ? '18px' : '16px'
                            }}>
                                {formatNumber(item.value)}<small style={{ fontSize: '12px', marginLeft: '2px' }}>грн</small>
                            </span>
                            {index < items.length - 1 && (
                                <div style={{ width: '1px', height: '24px', background: '#f0f0f0', marginLeft: '16px' }} />
                            )}
                        </div>
                    ))}
                </div>

                {isComparison && (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                        <Tag color={totals.hasDiscrepancy ? 'red' : 'green'} style={{
                            borderRadius: '4px',
                            border: 'none',
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '14px',
                            textTransform: 'uppercase'
                        }}>
                            {totals.hasDiscrepancy ? 'Є розбіжності' : 'Дані сходяться'}
                        </Tag>
                    </div>
                )}
            </div>
        </Card>
    );
};

const CommodityVerification = ({ currentContactStore, onTabChange, dailyData = [], monthlyData = [], mode = 'comparison', filterMonth, filterYear, filterText = '' }) => {

    const formatNumber = (number) => {
        if (number === undefined || number === null) return '0,00';
        return number
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            .replace('.', ',');
    };

    // Calculate target date based on filters or default to last month
    const targetDate = useMemo(() => {
        if (!filterMonth && !filterYear) {
            return moment().subtract(1, 'month').startOf('month');
        }

        const year = filterYear || moment().year();
        const month = (filterMonth || moment().month() + 1) - 1;

        return moment().year(year).month(month).startOf('month');
    }, [filterMonth, filterYear]);

    const isApproved = useMemo(() => {
        const monthlyReport = monthlyData.find(item => {
            const itemMonth = moment(item.date).startOf('month');
            return itemMonth.isSame(targetDate, 'month') && !item.isOnlineSales;
        });
        return !!(monthlyReport && (monthlyReport.turnoverApproving === true || monthlyReport.turnoverApproving === 'true'));
    }, [monthlyData, targetDate]);

    const processedData = useMemo(() => {
        const lastMonthDays = targetDate.daysInMonth();
        const dailyMap = {};

        // Initialize all days of the target month
        for (let i = 1; i <= lastMonthDays; i++) {
            const date = targetDate.clone().date(i).format('YYYY-MM-DD');
            dailyMap[date] = {
                date: targetDate.clone().date(i).format('DD.MM.YYYY'),
                dayOfWeek: targetDate.clone().date(i).format('ddd'),
                b2b: null,
                digital: null,
                hasDiscrepancy: false
            };
        }

        // Fill with real data
        dailyData.forEach(item => {
            const itemDate = moment(item.date).format('YYYY-MM-DD');
            if (dailyMap[itemDate]) {
                if (item.dataSource === SOURCE_IDS.B2B) {
                    dailyMap[itemDate].b2b = (dailyMap[itemDate].b2b === null ? 0 : dailyMap[itemDate].b2b) + (item.amountWithVAT || 0);
                } else if (item.dataSource === SOURCE_IDS.DIGITAL && isApproved) {
                    dailyMap[itemDate].digital = (dailyMap[itemDate].digital === null ? 0 : dailyMap[itemDate].digital) + (item.amountWithVAT || 0);
                }
            }
        });

        // Calculate discrepancies
        Object.values(dailyMap).forEach(day => {
            if (day.b2b === null && day.digital === null) {
                day.hasDiscrepancy = false;
            } else {
                const b2bVal = day.b2b === null ? 0 : day.b2b;
                const digitalVal = day.digital === null ? 0 : day.digital;
                day.hasDiscrepancy = Math.abs(b2bVal - digitalVal) > 0.01;
            }
        });

        let result = Object.values(dailyMap).sort((a, b) => moment(a.date, 'DD.MM.YYYY') - moment(b.date, 'DD.MM.YYYY'));

        if (filterText) {
            const search = filterText.toLowerCase();
            result = result.filter(day =>
                day.date.toLowerCase().includes(search) ||
                day.dayOfWeek.toLowerCase().includes(search)
            );
        }

        return result;
    }, [dailyData, targetDate, filterText, isApproved]);

    const totals = useMemo(() => {
        const b2bTotal = processedData.reduce((acc, curr) => acc + (curr.b2b === null ? 0 : curr.b2b), 0);
        const digitalTotal = processedData.reduce((acc, curr) => acc + (curr.digital === null ? 0 : curr.digital), 0);

        // Find monthly report for target month
        const monthlyReport = monthlyData.find(item => {
            const itemMonth = moment(item.date).startOf('month');
            return itemMonth.isSame(targetDate, 'month') && !item.isOnlineSales;
        });

        const monthlyTotal = monthlyReport ? monthlyReport.amountWithVAT : 0;

        // Discrepancy based on Monthly B2B vs Daily Digital Sum
        const hasDiscrepancy = Math.abs(digitalTotal - monthlyTotal) > 0.01;

        return { b2bTotal, digitalTotal, monthlyTotal, hasDiscrepancy };
    }, [processedData, monthlyData, targetDate]);

    const columns = useMemo(() => {
        if (mode === 'reconciliation') {
            return [
                {
                    title: 'Дата',
                    dataIndex: 'date',
                    key: 'date',
                    width: 200,
                    onCell: () => ({ style: { verticalAlign: 'middle' } }),
                    render: (text, record) => (
                        <div>
                            <div style={{ fontWeight: 600, color: '#2d1b4e' }}>{text}</div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'capitalize' }}>{record.dayOfWeek}</div>
                        </div>
                    )
                },
                {
                    title: 'Сума з ПДВ (Цифровий)',
                    dataIndex: 'digital',
                    key: 'digital',
                    align: 'right',
                    onCell: () => ({ style: { verticalAlign: 'middle' } }),
                    render: (val) => {
                        if (val === null) return <span style={{ color: '#bfbfbf' }}>—</span>;
                        return <span style={{ color: '#9A18FF', fontWeight: 600 }}>{formatNumber(val)} грн</span>;
                    },
                },
            ];
        }
        return [];
    }, [mode]);



    return (
        <div className="commodity-verification animate-fade-in" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <div className="commodity-form__section-header" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <GeneralSalesIcon />
                    <span className="commodity-form__section-title" style={{ fontSize: '20px', color: '#2d1b4e' }}>
                        {mode === 'comparison' ? 'Загальний товарообіг' : 'Цифровий ТО'} {targetDate.format('MMMM YYYY')}
                    </span>
                </div>
            </div>

            {!isApproved && mode === 'reconciliation' ? (
                <Card bordered={false} style={{
                    borderRadius: '12px',
                    textAlign: 'center',
                    padding: '48px 24px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    background: '#fff'
                }}>
                    <div style={{ fontSize: '16px', color: '#8c8c8c', fontWeight: 500 }}>
                        Цифровий товарообіг буде доступний після того, як щомісячний звіт за {targetDate.format('MMMM YYYY')} року буде затверджено.
                    </div>
                </Card>
            ) : (
                <>
                    <SummaryRow totals={totals} mode={mode} />

                    {mode === 'reconciliation' && (
                        <div style={{
                            background: '#fff',
                            borderRadius: '16px',
                            padding: '24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                            width: '100%',
                            overflowX: 'auto'
                        }}>
                            <Table
                                className="default-table request-table"
                                columns={columns}
                                dataSource={processedData}
                                pagination={false}
                                scroll={{ y: 550, x: 'max-content' }}
                                rowClassName={(record, index) => {
                                    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CommodityVerification;
